import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';
import { JsonRPCService } from '@xengage/gw-portals-transport-js';
import { MLClickToChatService, withRetry } from 'ml-services-common-react';
import appConfig from 'app-config';

const sendWithRetry = (endpoint, method, params, additionalHeaders) => withRetry(() => JsonRPCService.send(endpoint, method, params, additionalHeaders));

const { MLUserTypes } = appConfig;

let currentUser;
let cachingInProgress = false;

const setUserTypes = (aUser) => {
    Object.defineProperty(aUser, 'isIA', {
        get: function () { return this.userType === MLUserTypes.IA; }
    });
    Object.defineProperty(aUser, 'isPCS', {
        get: function () { return this.userType === MLUserTypes.PCS; }
    });
    Object.defineProperty(aUser, 'isGCSSales', {
        get: function () { return this.userType === MLUserTypes.GCS_SALES; }
    });
    Object.defineProperty(aUser, 'isGCSServicing', {
        get: function () { return this.userType === MLUserTypes.GCS_SERVICING; }
    });
    Object.defineProperty(aUser, 'isMetGA', {
        get: function () { return this.userType === MLUserTypes.MET_GA; }
    });
};

const shouldRefreshCurrentUser = (includeProducerCode) => {
    return includeProducerCode ? (!currentUser || !currentUser.producerCodes_ML) : !currentUser;
};

const until_Ext = (condition) => {
    const wait = (resolve) => {
        if (condition()) {
            resolve();
        } else {
            setTimeout(() => wait(resolve), 500);
        }
    }
    return new Promise(wait);
};

const callIdentifyAnalyticsApi = (user, header) => {
    try {
        let loginID, portalId;
        if(user.userTypeName_ML === 'IA') {
            const JWTToken=JSON.stringify(header);
            const token = JSON.parse(JWTToken).Authorization;
            const base64Url = token.split(".")[1];
            const base64 = base64Url.replace("-", "+").replace("_", "/");
            const result = JSON.parse(window.atob(base64));            
            
            loginID = result.pc_username;
            user.loginID  = loginID; 
            portalId = (result.farmers_portal_id) ? result.farmers_portal_id[0] : undefined;
            user.portalId = portalId;
        }
        
        window.analytics.identify(user.userName_ML, {
            name: user.displayName,
            email: user.email,
            producercode: user.primaryProducerCode_ML?.code,
            userType: user.userTypeName_ML,
            pc_username: user.userName_ML,
            groupName: user.affiliatedGroup_ML?.groupName,
            login : loginID,
            IAPortalId: portalId
        });
    } catch (e) {
        console.error(`Segment error calling identify API: ${e.message}`);
    }
};

const getPrimaryProducerCodeAndGroup_Ext = (additionalHeaders = {}) => {
    sendWithRetry(getProxiedServiceUrl('gatewayUser'), 'getPrimaryProducerCodeAndGroup',
        [], additionalHeaders).then((userInfo) => {
        currentUser.primaryProducerCode_ML = userInfo.primaryProducerCode_ML;
        currentUser.affiliatedGroup_ML = userInfo.affiliatedGroup_ML; 

        callIdentifyAnalyticsApi(currentUser, additionalHeaders);

        MLClickToChatService.updateAgentCode({
            'Agent_Code__c': userInfo.primaryProducerCode_ML.code
        })
    });
}

const getAndCacheCurrentUser_Ext = async (additionalHeaders, includeProducerCode, forceRefresh) => {
    await until_Ext(() => cachingInProgress === false);
    
    if (shouldRefreshCurrentUser(includeProducerCode) || forceRefresh) {
        cachingInProgress = true;
        
        return sendWithRetry(getProxiedServiceUrl('gatewayUser'), 'getActiveUser',
            [includeProducerCode], additionalHeaders).then((user) => {                
            // Retrieve primary producer code and group information separately during login
            if (!currentUser || !currentUser.primaryProducerCode_ML || !currentUser.affiliatedGroup_ML || forceRefresh) {
                getPrimaryProducerCodeAndGroup_Ext(additionalHeaders);
            } else {
                // includeProducerCode flag when true will return producer codes for a group
                // this flag is false during login so we update the group with producer codes
                user.primaryProducerCode_ML = currentUser.primaryProducerCode_ML;
            }           

            currentUser = user;
            setUserTypes(user);
            return user;
        }).finally(() => {
            cachingInProgress = false;
        });
    }

    return Promise.resolve(currentUser);
};

export default class UserService {

    static getAvailableProducerCodesForCurrentUser(additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayUser'),
            'getAvailableProducerCodesForCurrentUser', [], additionalHeaders);
    }

    static hasUserSystemPermission(permission, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayUser'), 'hasUserSystemPermission',
            [permission], additionalHeaders);
    }

    static hasUserPermission(permission, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayUser'), 'hasUserPermission',
            [permission], additionalHeaders);
    }

    static hasUserAccessToAccountPolicies(accountNumber, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayUser'), 'hasUserAccessToAccountPolicies',
            [accountNumber], additionalHeaders);
    }

    /**
     * This is the server responsible for dealing with the Gateway Current User Information
     * whic is used in the AuthenticationContext.jsx
     * */

    static getGatewayCurrentUser(additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayUser'), 'getCurrentUser',
            [], additionalHeaders);
    }

    static getVueSystemLink(additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayUser'), 'getVueSystemLink',
            [], additionalHeaders);
    }

    static getCurrentUser(additionalHeaders = {}, includeProducerCode = false, forceRefresh = false) {
        return getAndCacheCurrentUser_Ext(additionalHeaders, includeProducerCode, forceRefresh);
    }

    static getProducerCodesForGroup(groupPublicID, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayUser'), 'getProducerCodesForGroup', [groupPublicID], additionalHeaders);
    }

    static getUserAvailableGroups(additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayUser'), 'getUserAvailableGroups',
            [], additionalHeaders);
    }

    static setPrimaryProducerCodeOverride(producerCode, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayUser'), 'setPrimaryProducerCodeOverride',
            [producerCode], additionalHeaders);
    }

     static getFormostProducersForCrossSell(farmersPortalId, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayUser'), 'getFormostProducersForCrossSell',
            [farmersPortalId], additionalHeaders);
    }
    
    static getPrimaryProducerCodeAndGroup(additionalHeaders = {}) {
        return  sendWithRetry(getProxiedServiceUrl('gatewayUser'), 'getPrimaryProducerCodeAndGroup',
        [], additionalHeaders)
    }

}
